import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import dayjs from 'dayjs';
import { BusinessService } from './business.service';
import { UserService } from '../user/user.service';
import { DisconnectedIntegration } from './business';

export const DISCONNECTED_INTEGRATION_TOAST_ID = 'disconnected-integration';

const SUMMARY_KEY = 'APP.META_DISCONNECTED_TOAST.SUMMARY';
const DETAIL_KEY = 'APP.META_DISCONNECTED_TOAST.DETAIL';

/**
 * Error toast for a Messenger or Instagram integration the API uninstalled
 * because Meta invalidated its token (password change, session reset). The
 * API stops listing an integration once it is reconnected, so the toast
 * resolves itself; closing it hides it until Meta disconnects one again.
 */
@Injectable({
  providedIn: 'root'
})
export class DisconnectedIntegrationToastService {
  private shownForBusinessId?: string;

  constructor(
    private messageService: MessageService,
    private translate: TranslateService,
    private business: BusinessService,
    private user: UserService,
  ) {
  }

  get undismissedIntegrations(): DisconnectedIntegration[] {
    const dismissedAt = this.business.disconnectedIntegrationToastDismissedAt;

    if (!dismissedAt) {
      return this.business.disconnectedIntegrations;
    }

    return this.business.disconnectedIntegrations
      .filter((integration) => dayjs(integration.disconnectedAt).isAfter(dayjs(dismissedAt)));
  }

  showIfNeeded(): void {
    const businessId = this.user.activeBusiness?.id;

    if (!businessId || this.shownForBusinessId === businessId) {
      return;
    }

    const integrations = this.undismissedIntegrations;

    if (!integrations.length) {
      return;
    }

    this.shownForBusinessId = businessId;
    const appNames = integrations.map((integration) => integration.name).join(' and ');

    this.translate.get([SUMMARY_KEY, DETAIL_KEY], { appNames }).subscribe((copy: Record<string, string>) => {
      this.messageService.add({
        severity: 'error',
        sticky: true,
        styleClass: 'p-toast-message-wide',
        summary: copy[SUMMARY_KEY],
        detail: copy[DETAIL_KEY],
        data: { id: DISCONNECTED_INTEGRATION_TOAST_ID },
      });
    });
  }

  handleClose(message?: ToastMessageOptions): void {
    if (message?.data?.id !== DISCONNECTED_INTEGRATION_TOAST_ID) {
      return;
    }

    this.business.dismissDisconnectedIntegrationToast().subscribe();
  }
}
